import { Currency } from '../billing.config'
import type { RazorpayMethod } from './razorpay-method-picker'

// Which recharge methods a Razorpay account is offered, and which one is
// effectively selected. Pure and shared by every Razorpay entry point, so the
// availability rules can't drift per call site — they used to live inside one
// modal only, and the other silently offered card alone.

interface RazorpayMethodAvailabilityParams {
  // The add-card entry point vaults a card; the bank rails have nothing to vault,
  // so they are never offered there.
  isAddCardIntent: boolean
  currency: string
  isEnterprisePlan: boolean
}

export interface RazorpayMethodAvailability {
  showNetbanking: boolean
  showBankTransfer: boolean
  availableMethods: RazorpayMethod[]
}

// Netbanking is a publicly released method, so every INR account gets it with no
// flag gate — note it is offered here but SETTLED BY CASHFREE (Razorpay does not
// support netbanking); this policy decides what is on the menu, not who runs it.
// Bank transfer (Smart Collect virtual account) is GA and carries no rollout
// flag. It stays Enterprise-gated — that is product policy for the rail, not a
// rollout gate, so do not read the two as one thing. The currency guard is
// explicit rather than inherited from the provider resolver: a virtual account
// is an Indian rail and each rail's INR floor is a no-op outside INR.
export const getRazorpayMethodAvailability = ({
  isAddCardIntent,
  currency,
  isEnterprisePlan,
}: RazorpayMethodAvailabilityParams): RazorpayMethodAvailability => {
  const isINR = currency === Currency.INR
  const showNetbanking = !isAddCardIntent && isINR
  const showBankTransfer = !isAddCardIntent && isINR && isEnterprisePlan
  return {
    showNetbanking,
    showBankTransfer,
    availableMethods: [
      'card',
      ...(showNetbanking ? (['netbanking'] as const) : []),
      ...(showBankTransfer ? (['bank_transfer'] as const) : []),
    ],
  }
}

interface ResolveRazorpayMethodParams {
  restrictToMethod?: RazorpayMethod
  userSelectedMethod: RazorpayMethod
  availableMethods: RazorpayMethod[]
  // Set when the chosen amount is below the floor of the rail the user selected.
  // Per-rail, not shared: netbanking and bank transfer have different floors.
  selectedRailDisabledReason?: string
}

// A restricted entry point pins the method. Otherwise fall back to card when the
// selection is no longer offered (plan changed) or has dropped below its own
// rail's floor; re-engages if the amount goes back up.
export const resolveRazorpayMethod = ({
  restrictToMethod,
  userSelectedMethod,
  availableMethods,
  selectedRailDisabledReason,
}: ResolveRazorpayMethodParams): RazorpayMethod => {
  if (restrictToMethod) return restrictToMethod
  if (!availableMethods.includes(userSelectedMethod)) return 'card'
  if (userSelectedMethod !== 'card' && selectedRailDisabledReason) return 'card'
  return userSelectedMethod
}
